"use client";

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Check, Info, LayoutGrid, Loader2, Search, Table2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { searchInputOverlayIconClass } from "@/lib/searchInputIcon";
import {
  ItemSelectorResolvedDetailsBody,
  type ItemSelectorDetailFieldMeta,
} from "@/components/itemSelectorDetailResolve";

export type ItemSelectorTableColumn = {
  key: string;
  header: string;
  className?: string;
};

export type ItemSelectorItem<T = unknown> = {
  value: string;
  label: string;
  subtitle?: string;
  /** Display values keyed by {@link ItemSelectorTableColumn.key}. */
  cells?: Record<string, string>;
  disabled?: boolean;
  data?: T;
};

export type ItemSelectorViewMode = "grid" | "table";

/** `publicForm`: labels only (same as public form dropdown); no UIDs or extra fields in details. */
export type ItemSelectorVariant = "default" | "publicForm";

const VIEW_MODE_STORAGE_PREFIX = "labwick.itemSelector.view.";

export type ItemSelectorDialogProps<T = unknown> = {
  variant?: ItemSelectorVariant;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  items: ItemSelectorItem<T>[];
  title?: string;
  description?: string;
  tableColumns?: ItemSelectorTableColumn[];
  /** Initial layout when no saved preference exists. */
  defaultViewMode?: ItemSelectorViewMode;
  /** Persist grid/table choice in localStorage (per key). */
  viewModeStorageKey?: string;
  /** Column count for grid layout. */
  gridColumns?: 1 | 2 | 3 | 4;
  /** Current value when the dialog opens (single string or multiple). */
  value?: string | string[];
  multiple?: boolean;
  allowEmpty?: boolean;
  loading?: boolean;
  searchPlaceholder?: string;
  emptyMessage?: string;
  selectLabel?: string;
  cancelLabel?: string;
  onConfirm: (value: string | string[], items: ItemSelectorItem<T> | ItemSelectorItem<T>[]) => void;
  filterItem?: (item: ItemSelectorItem<T>, search: string) => boolean;
  renderItem?: (item: ItemSelectorItem<T>, selected: boolean) => ReactNode;
  renderItemDetails?: (item: ItemSelectorItem<T>) => ReactNode;
  /** Related collection field defs — relation UIDs are resolved to labels in the details dialog. */
  detailCollectionFields?: ItemSelectorDetailFieldMeta[];
};

function itemSelectorCellValue<T>(item: ItemSelectorItem<T>, key: string): string {
  const fromCells = item.cells?.[key];
  if (fromCells != null && String(fromCells).trim() !== "") return String(fromCells);
  if (key === "label") return item.label;
  if (key === "subtitle") return item.subtitle ?? "";
  if (key === "value" || key === "uid") return item.value;
  return "";
}

export function defaultItemSelectorFilter<T>(
  item: ItemSelectorItem<T>,
  search: string
): boolean {
  const q = search.trim().toLowerCase();
  if (!q) return true;
  const parts = [
    item.label,
    item.subtitle,
    item.value,
    ...(item.cells ? Object.values(item.cells) : []),
  ];
  const haystack = parts
    .filter((p) => p != null && String(p).trim() !== "")
    .join(" ")
    .toLowerCase();
  return haystack.includes(q);
}

export function publicFormItemSelectorFilter<T>(
  item: ItemSelectorItem<T>,
  search: string
): boolean {
  const q = search.trim().toLowerCase();
  if (!q) return true;
  return item.label.toLowerCase().includes(q);
}

function isSelectableSelectorItem<T>(item: ItemSelectorItem<T>): boolean {
  return !item.disabled && String(item.value).trim() !== "";
}

function normalizeValue(value: string | string[] | undefined, multiple: boolean): string[] {
  if (multiple) {
    if (Array.isArray(value)) return value.map(String);
    if (value != null && value !== "") return [String(value)];
    return [];
  }
  if (Array.isArray(value)) return value.length ? [String(value[0])] : [];
  if (value != null && value !== "") return [String(value)];
  return [];
}

function defaultTableColumns<T>(items: ItemSelectorItem<T>[]): ItemSelectorTableColumn[] {
  const cellKeys = new Set<string>();
  for (const item of items) {
    if (item.cells) {
      for (const key of Object.keys(item.cells)) cellKeys.add(key);
    }
  }
  if (cellKeys.size > 0) {
    return [...cellKeys].map((key) => ({
      key,
      header: key === "uid" ? "UID" : key.charAt(0).toUpperCase() + key.slice(1),
    }));
  }
  const cols: ItemSelectorTableColumn[] = [{ key: "label", header: "Name" }];
  if (items.some((i) => i.subtitle)) {
    cols.push({ key: "subtitle", header: "ID" });
  } else if (items.some((i) => i.value)) {
    cols.push({ key: "value", header: "UID" });
  }
  return cols;
}

const gridColumnClass: Record<1 | 2 | 3 | 4, string> = {
  1: "grid-cols-1",
  2: "grid-cols-1 sm:grid-cols-2",
  3: "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3",
  4: "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4",
};

function readStoredViewMode(storageKey: string | undefined): ItemSelectorViewMode | null {
  if (!storageKey || typeof window === "undefined") return null;
  const stored = localStorage.getItem(VIEW_MODE_STORAGE_PREFIX + storageKey);
  if (stored === "grid" || stored === "table") return stored;
  return null;
}

function writeStoredViewMode(storageKey: string | undefined, mode: ItemSelectorViewMode) {
  if (!storageKey || typeof window === "undefined") return;
  localStorage.setItem(VIEW_MODE_STORAGE_PREFIX + storageKey, mode);
}

function itemHasDetails<T>(
  item: ItemSelectorItem<T>,
  variant: ItemSelectorVariant
): boolean {
  if (variant === "publicForm") {
    return Boolean(item.value && String(item.label || "").trim());
  }
  if (item.data != null && typeof item.data === "object" && !Array.isArray(item.data)) {
    return Object.keys(item.data as object).length > 0;
  }
  return Boolean(item.label || item.value || (item.cells && Object.keys(item.cells).length > 0));
}

function ItemSelectorPublicFormDetailsBody<T>({ item }: { item: ItemSelectorItem<T> }) {
  const text = String(item.label || "").trim();
  return (
    <p className="text-sm leading-relaxed text-foreground">{text || "—"}</p>
  );
}

function ItemSelectorInfoButton<T>({
  item,
  variant,
  onShow,
}: {
  item: ItemSelectorItem<T>;
  variant: ItemSelectorVariant;
  onShow: (item: ItemSelectorItem<T>) => void;
}) {
  if (!itemHasDetails(item, variant)) return null;
  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      className="h-7 w-7 shrink-0 text-muted-foreground hover:text-foreground"
      title="View all details"
      aria-label={`View details for ${item.label || item.value || "item"}`}
      onClick={(e) => {
        e.stopPropagation();
        onShow(item);
      }}
    >
      <Info className="h-4 w-4" />
    </Button>
  );
}

export function ItemSelectorDialog<T = unknown>({
  open,
  onOpenChange,
  items,
  variant = "default",
  title = "Select item",
  description,
  tableColumns,
  defaultViewMode,
  viewModeStorageKey,
  gridColumns = 3,
  value,
  multiple = false,
  allowEmpty = false,
  loading = false,
  searchPlaceholder = "Search…",
  emptyMessage = "No items match your search.",
  selectLabel = "Select",
  cancelLabel = "Cancel",
  onConfirm,
  filterItem,
  renderItem,
  renderItemDetails,
  detailCollectionFields,
}: ItemSelectorDialogProps<T>) {
  const isPublicForm = variant === "publicForm";
  const effectiveDefaultViewMode: ItemSelectorViewMode =
    defaultViewMode ?? "table";
  const effectiveFilterItem =
    filterItem ?? (isPublicForm ? publicFormItemSelectorFilter : defaultItemSelectorFilter);

  const [search, setSearch] = useState("");
  const [draft, setDraft] = useState<string[]>([]);
  const [viewMode, setViewMode] = useState<ItemSelectorViewMode>(effectiveDefaultViewMode);
  const [detailsItem, setDetailsItem] = useState<ItemSelectorItem<T> | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const resolvedColumns = useMemo(() => {
    if (isPublicForm) {
      const header =
        tableColumns?.find((c) => c.key === "label")?.header ??
        tableColumns?.[0]?.header ??
        "Name";
      return [{ key: "label", header }];
    }
    return tableColumns?.length ? tableColumns : defaultTableColumns(items);
  }, [isPublicForm, tableColumns, items]);

  const tableViewAvailable = resolvedColumns.length > 0;

  useEffect(() => {
    if (!open) {
      setDetailsItem(null);
      return;
    }
    setSearch("");
    setDraft(normalizeValue(value, multiple));
    const stored = readStoredViewMode(viewModeStorageKey);
    if (stored === "grid") {
      setViewMode("grid");
    } else if (stored === "table" && tableViewAvailable) {
      setViewMode("table");
    } else {
      setViewMode(
        tableViewAvailable && effectiveDefaultViewMode === "table" ? "table" : "grid"
      );
    }
  }, [
    open,
    value,
    multiple,
    viewModeStorageKey,
    effectiveDefaultViewMode,
    tableViewAvailable,
  ]);

  const setViewModePersisted = useCallback(
    (mode: ItemSelectorViewMode) => {
      const next = mode === "table" && !tableViewAvailable ? "grid" : mode;
      setViewMode(next);
      writeStoredViewMode(viewModeStorageKey, next);
    },
    [viewModeStorageKey, tableViewAvailable]
  );

  const activeViewMode =
    viewMode === "table" && tableViewAvailable ? "table" : "grid";

  /** Hide column headings when a single data column duplicates the dialog title. */
  const showTableColumnHeader = resolvedColumns.length > 1;

  const filteredItems = useMemo(() => {
    if (!search.trim()) return items;
    return items.filter((item) => effectiveFilterItem(item, search));
  }, [items, search, effectiveFilterItem]);

  const soleSelectableItem = useMemo(() => {
    const selectable = filteredItems.filter(isSelectableSelectorItem);
    return selectable.length === 1 ? selectable[0] : null;
  }, [filteredItems]);

  useEffect(() => {
    if (!open || loading || !soleSelectableItem) return;
    const onlyValue = soleSelectableItem.value;
    if (multiple) {
      setDraft((prev) => {
        if (prev.includes(onlyValue)) return prev;
        return [...prev, onlyValue];
      });
      return;
    }
    setDraft([onlyValue]);
  }, [open, loading, soleSelectableItem, multiple]);

  const selectedSet = useMemo(() => new Set(draft), [draft]);

  const toggleItem = useCallback(
    (item: ItemSelectorItem<T>) => {
      if (item.disabled) return;
      const itemValue = item.value;
      if (multiple) {
        setDraft((prev) => {
          const next = new Set(prev);
          if (next.has(itemValue)) next.delete(itemValue);
          else next.add(itemValue);
          return [...next];
        });
        return;
      }
      setDraft([itemValue]);
    },
    [multiple]
  );

  const canConfirm =
    allowEmpty || draft.length > 0 || (multiple && draft.length === 0);

  const handleConfirm = () => {
    const selectedItems = items.filter((item) => selectedSet.has(item.value));
    if (multiple) {
      onConfirm(draft, selectedItems);
    } else {
      const single = draft[0] ?? "";
      const one = selectedItems[0] ?? items.find((i) => i.value === single);
      onConfirm(single, one ?? { value: single, label: single });
    }
    onOpenChange(false);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          className="flex max-h-[min(90vh,720px)] flex-col gap-0 overflow-hidden p-0 sm:max-w-4xl [&>button]:z-30"
          onOpenAutoFocus={(event) => {
            event.preventDefault();
            requestAnimationFrame(() => searchInputRef.current?.focus());
          }}
        >
          <div className="relative z-20 shrink-0 border-b bg-background px-6 pb-4 pt-6 pr-12">
            <DialogHeader className="space-y-1.5 text-left">
              <DialogTitle>{title}</DialogTitle>
              {description ? (
                <DialogDescription>{description}</DialogDescription>
              ) : null}
            </DialogHeader>

            <div className="mt-4 flex items-center gap-2">
              <div className="relative min-w-0 flex-1">
                <Search className={searchInputOverlayIconClass()} aria-hidden />
                <Input
                  ref={searchInputRef}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={searchPlaceholder}
                  className="pl-9"
                  onKeyDown={(event) => {
                    if (event.key !== "Enter" || !soleSelectableItem) return;
                    event.preventDefault();
                    const soleValue = soleSelectableItem.value;
                    const isSelected = draft.includes(soleValue);
                    if (isSelected && canConfirm) {
                      handleConfirm();
                      return;
                    }
                    toggleItem(soleSelectableItem);
                  }}
                />
              </div>
              {tableViewAvailable ? (
                <div
                  className="flex shrink-0 rounded-md border p-0.5"
                  role="group"
                  aria-label="View layout"
                >
                  <Button
                    type="button"
                    variant={activeViewMode === "grid" ? "secondary" : "ghost"}
                    size="icon"
                    className="h-8 w-8"
                    title="Grid view"
                    aria-pressed={activeViewMode === "grid"}
                    onClick={() => setViewModePersisted("grid")}
                  >
                    <LayoutGrid className="h-4 w-4" />
                  </Button>
                  <Button
                    type="button"
                    variant={activeViewMode === "table" ? "secondary" : "ghost"}
                    size="icon"
                    className="h-8 w-8"
                    title="Table view"
                    aria-pressed={activeViewMode === "table"}
                    onClick={() => setViewModePersisted("table")}
                  >
                    <Table2 className="h-4 w-4" />
                  </Button>
                </div>
              ) : null}
            </div>
          </div>

          <div
            className={cn(
              "relative z-0 min-h-[200px] flex-1 overflow-y-auto overscroll-contain px-6 py-4",
              activeViewMode === "table" && "pt-2.5"
            )}
          >
            <div
              className={cn(
                activeViewMode === "table" && "rounded-md border-none"
              )}
            >
              {loading ? (
                <div className="flex h-40 items-center justify-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading…
                </div>
              ) : filteredItems.length === 0 ? (
                <p className="py-10 text-center text-sm text-muted-foreground">
                  {emptyMessage}
                </p>
              ) : activeViewMode === "table" ? (
                <Table className="border-separate border-spacing-0">
                  {showTableColumnHeader ? (
                    <TableHeader className="sticky top-0 z-10 bg-background shadow-sm">
                      <TableRow className="border-b bg-background hover:bg-background">
                        <TableHead className="sticky top-0 z-10 w-10 bg-background px-2" aria-label="Selected" />
                        {resolvedColumns.map((col) => (
                          <TableHead
                            key={col.key}
                            className={cn(
                              "sticky top-0 z-10 bg-background",
                              col.className
                            )}
                          >
                            {col.header}
                          </TableHead>
                        ))}
                        {!isPublicForm ? (
                          <TableHead
                            className="sticky top-0 z-10 w-10 bg-background px-1"
                            aria-label="Details"
                          />
                        ) : null}
                      </TableRow>
                    </TableHeader>
                  ) : null}
                  <TableBody className="relative z-0">
                    {filteredItems.map((item) => {
                      const selected = selectedSet.has(item.value);
                      return (
                        <TableRow
                          key={item.value || "__empty__"}
                          role="option"
                          aria-selected={selected}
                          data-state={selected ? "selected" : undefined}
                          className={cn(
                            "relative z-0 cursor-pointer",
                            selected && "bg-primary/5 hover:bg-primary/10",
                            item.disabled && "cursor-not-allowed opacity-50"
                          )}
                          onClick={() => toggleItem(item)}
                        >
                          <TableCell className="w-10 px-2">
                            {selected ? (
                              <Check className="h-4 w-4 text-primary" aria-hidden />
                            ) : null}
                          </TableCell>
                          {resolvedColumns.map((col) => {
                            const text = itemSelectorCellValue(item, col.key);
                            return (
                              <TableCell
                                key={col.key}
                                className={cn("max-w-[240px] truncate", col.className)}
                                title={text || undefined}
                              >
                                {text || "—"}
                              </TableCell>
                            );
                          })}
                          {!isPublicForm ? (
                            <TableCell className="w-10 px-1">
                              <ItemSelectorInfoButton
                                item={item}
                                variant={variant}
                                onShow={setDetailsItem}
                              />
                            </TableCell>
                          ) : null}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              ) : (
                <div
                  role="listbox"
                  aria-multiselectable={multiple || undefined}
                  className={cn("grid gap-2 p-0.5", gridColumnClass[gridColumns])}
                >
                  {filteredItems.map((item) => {
                    const selected = selectedSet.has(item.value);
                    return (
                      <div
                        key={item.value || "__empty__"}
                        role="option"
                        aria-selected={selected}
                        tabIndex={item.disabled ? -1 : 0}
                        onClick={() => toggleItem(item)}
                        onKeyDown={(e) => {
                          if (item.disabled) return;
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            toggleItem(item);
                          }
                        }}
                        className={cn(
                          "relative flex min-h-[3.25rem] cursor-pointer flex-col items-start rounded-md border px-3 py-2.5 text-left text-sm transition-colors",
                          isPublicForm ? "pr-3" : "pr-12",
                          "hover:bg-accent/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
                          selected
                            ? "border-primary bg-primary/5 ring-1 ring-primary"
                            : "border-border bg-background",
                          item.disabled && "cursor-not-allowed opacity-50"
                        )}
                      >
                        {selected ? (
                          <Check
                            className={cn(
                              "absolute top-2 h-4 w-4 text-primary",
                              isPublicForm ? "right-2" : "right-8"
                            )}
                            aria-hidden
                          />
                        ) : null}
                        {!isPublicForm ? (
                          <div className="absolute right-1 top-1">
                            <ItemSelectorInfoButton
                              item={item}
                              variant={variant}
                              onShow={setDetailsItem}
                            />
                          </div>
                        ) : null}
                        {renderItem ? (
                          renderItem(item, selected)
                        ) : (
                          <>
                            <span className="line-clamp-2 font-medium leading-snug">
                              {item.label}
                            </span>
                            {!isPublicForm && item.subtitle ? (
                              <span className="mt-0.5 line-clamp-1 text-xs text-muted-foreground">
                                {item.subtitle}
                              </span>
                            ) : null}
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          <DialogFooter className="relative z-20 shrink-0 gap-2 border-t bg-background px-6 py-4 sm:justify-end">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {cancelLabel}
            </Button>
            <Button
              type="button"
              variant="primary"
              disabled={!canConfirm || loading}
              onClick={handleConfirm}
            >
              {selectLabel}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={detailsItem != null}
        onOpenChange={(next) => {
          if (!next) setDetailsItem(null);
        }}
      >
        <DialogContent className="flex max-h-[min(85vh,560px)] flex-col gap-4 overflow-hidden sm:max-w-lg">
          <DialogHeader className="text-left">
            <DialogTitle>
              {isPublicForm ? "Details" : detailsItem?.label || "Item details"}
            </DialogTitle>
            {!isPublicForm && detailsItem?.value ? (
              <DialogDescription className="font-mono text-xs">
                {detailsItem.value}
              </DialogDescription>
            ) : null}
          </DialogHeader>
          {detailsItem ? (
            renderItemDetails ? (
              renderItemDetails(detailsItem)
            ) : isPublicForm ? (
              <ItemSelectorPublicFormDetailsBody item={detailsItem} />
            ) : (
              <ItemSelectorResolvedDetailsBody
                item={detailsItem}
                collectionFields={detailCollectionFields}
              />
            )
          ) : null}
          <DialogFooter className="sm:justify-end">
            <Button type="button" variant="outline" onClick={() => setDetailsItem(null)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export type ItemSelectorFieldProps<T = unknown> = Omit<
  ItemSelectorDialogProps<T>,
  "open" | "onOpenChange" | "onConfirm"
> & {
  value?: string | string[];
  onChange: (value: string | string[], items: ItemSelectorItem<T> | ItemSelectorItem<T>[]) => void;
  placeholder?: string;
  disabled?: boolean;
  triggerClassName?: string;
};

/** Button trigger that opens {@link ItemSelectorDialog} (drop-in replacement for long dropdowns). */
export function ItemSelectorField<T = unknown>({
  value,
  onChange,
  items,
  placeholder = "Select…",
  disabled = false,
  triggerClassName,
  multiple = false,
  ...dialogProps
}: ItemSelectorFieldProps<T>) {
  const [open, setOpen] = useState(false);

  const displayLabel = useMemo(() => {
    const selected = normalizeValue(value, multiple);
    if (!selected.length) return null;
    const labels = selected
      .map((v) => items.find((i) => i.value === v)?.label ?? v)
      .filter(Boolean);
    return labels.join(", ");
  }, [value, items, multiple]);

  return (
    <>
      <Button
        type="button"
        variant="outline"
        disabled={disabled}
        className={cn(
          "h-9 w-full justify-between font-normal",
          !displayLabel && "text-muted-foreground",
          triggerClassName
        )}
        onClick={() => setOpen(true)}
      >
        <span className="truncate">{displayLabel ?? placeholder}</span>
      </Button>
      <ItemSelectorDialog
        {...dialogProps}
        open={open}
        onOpenChange={setOpen}
        value={value}
        multiple={multiple}
        items={items}
        onConfirm={onChange}
      />
    </>
  );
}
